import type { HomeStorySurfaceId } from "./home-story-surfaces";

export type Vec3 = [number, number, number];

export type HomeStoryPartId = "bar" | "left" | "middle" | "orb";

/** 滚动停靠点：开场、定模型、材质、工艺、素材、合成情绪板。 */
export const STORY_STOPS = [0.03, 0.21, 0.39, 0.57, 0.75, 0.94] as const;

/** 相邻停靠点的中点即章节切换点，文字层与 3D 层共用。 */
export const STORY_CHAPTER_EDGES = STORY_STOPS.slice(1).map((stop, index) => (STORY_STOPS[index] + stop) / 2);

/** 每个停靠点上各部件的表面：品牌亮面 → 白模 → 材质 → 工艺 → 素材（保持工艺）→ 最终配色：暖石材 + 胡桃木 + 黄铜，品牌青色只做一处点缀。 */
export const partSurfaces: Record<HomeStoryPartId, HomeStorySurfaceId[]> = {
  bar: ["gloss", "clay", "walnut", "steel", "steel", "travertine"],
  left: ["gloss", "clay", "terrazzo", "fluted", "fluted", "gloss"],
  middle: ["gloss", "clay", "travertine", "chrome", "chrome", "walnut"],
  orb: ["gloss", "clay", "chrome", "glaze", "glaze", "brass"],
};

export const storyParts: HomeStoryPartId[] = ["bar", "left", "middle", "orb"];

/** 定模型时部件先拆开再落回原位，像把模型摆上底板。 */
export const assembleVector: Record<HomeStoryPartId, Vec3> = {
  bar: [0.3, 1.5, -0.4],
  left: [-1.3, 0.2, 0.8],
  middle: [-0.2, -1.1, 1.4],
  orb: [1.2, 1.1, 1.8],
};

export function clamp01(value: number) {
  return Math.min(1, Math.max(0, value));
}

export function easeInOut(value: number) {
  return value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;
}

/** 第 `stop` 个停靠点之前的过渡进度（0→1），按序号错开，制造先后落位的节奏。 */
export function transitionAt(progress: number, stop: number, order = 0) {
  const center = STORY_CHAPTER_EDGES[stop - 1];
  const start = center - 0.055 + order * 0.018;
  return easeInOut(clamp01((progress - start) / 0.11));
}

/** 当前所处停靠点：过渡过半即视为进入下一章。 */
export function surfaceIndexAt(progress: number, order: number) {
  let index = 0;
  for (let stop = 1; stop < STORY_STOPS.length; stop++) {
    if (transitionAt(progress, stop, order) >= 0.5) index = stop;
  }
  return index;
}

export type StoryKey = { at: number; camera: Vec3; target: Vec3; logo: Vec3; spin: Vec3 };

/** 机位：开场正侧 → 定模型高位俯看网格 → 材质贴近 → 工艺低机位 → 素材拉远 → 情绪板正面退远。 */
export function buildStoryKeys(compact: boolean): StoryKey[] {
  const shift = compact ? 0 : 2.3;
  const lift = compact ? 2.2 : 0;
  const distance = compact ? 1.55 : 1;
  const key = (at: number, camera: Vec3, spin: Vec3, logoX = shift): StoryKey => ({
    at,
    camera: [camera[0] * distance + logoX * 0.15, camera[1], camera[2] * distance],
    target: [logoX * 0.42, lift * 0.55, 0],
    logo: [logoX, lift, 0],
    spin,
  });
  return [
    key(0, [0, 0.3, 10.6], [0.1, -0.46, 0]),
    key(STORY_STOPS[1], [-3.4, 3.2, 9.6], [0.06, 0.52, 0]),
    key(STORY_STOPS[2], [-0.8, 0.8, 9.2], [0.16, 0.42, -0.04]),
    key(STORY_STOPS[3], [0.5, -1.5, 10], [-0.14, -0.62, 0.05]),
    key(STORY_STOPS[4], [0.6, 0.9, 13.4], [0.06, -0.3, 0], compact ? 0 : 3.6),
    key(STORY_STOPS[5], [0, 0.5, 15], [0.04, -0.18, 0], compact ? 0 : 4.5),
    key(1, [0, 0.5, 15], [0.04, -0.18, 0], compact ? 0 : 4.5),
  ];
}
